/**
 * Settings › Billing: who statements are from, VAT, numbering, payment
 * terms and how to pay. Issued statements keep the details they were issued
 * with; a change here shows on the next.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Group, Loader, NumberInput, SimpleGrid, Stack, Switch, Text, TextInput, Textarea, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useBillingSettings, useSaveBillingSettings, type BillingSettings } from '../../api/statements';
import { useVocab } from '../../vocab';

export function BillingSettingsTab() {
  const v = useVocab();
  const { data, isPending, error } = useBillingSettings();
  const save = useSaveBillingSettings();
  const [d, setD] = useState<BillingSettings | null>(null);
  useEffect(() => {
    if (data) setD(data);
  }, [data]);
  if (isPending || !d) return error ? <Alert color="red" variant="light">{error.message}</Alert> : <Loader />;
  const set = <K extends keyof BillingSettings>(k: K, value: BillingSettings[K]) => setD({ ...d, [k]: value });

  async function submit() {
    if (!d) return;
    try {
      const next = await save.mutateAsync(d);
      setD(next);
      notifications.show({ color: 'teal', title: 'Saved', message: 'Billing details saved. Issued statements keep their own.' });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  return (
    <Stack maw={760}>
      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>Your organisation</Title>
          <Text size="sm" c="dimmed">Printed at the top of every statement.</Text>
          <TextInput label="Legal name" value={d.legal_name} onChange={(e) => set('legal_name', e.currentTarget.value)} />
          <Textarea label="Address" autosize minRows={3} value={d.address} onChange={(e) => set('address', e.currentTarget.value)} />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label="Company number" value={d.company_number} onChange={(e) => set('company_number', e.currentTarget.value)} />
            <TextInput label="VAT number" value={d.vat_number} onChange={(e) => set('vat_number', e.currentTarget.value)} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>VAT and terms</Title>
          <Switch
            label="VAT registered"
            description={`${v.One('buyer')} statements add VAT to the net total, worked once on the total and rounded to the penny.`}
            checked={d.vat_registered}
            onChange={(e) => set('vat_registered', e.currentTarget.checked)}
          />
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <NumberInput
              label="VAT rate" suffix="%" min={0} max={100} decimalScale={2}
              disabled={!d.vat_registered}
              value={d.vat_rate_pct}
              onChange={(n) => set('vat_rate_pct', typeof n === 'number' ? n : 0)}
            />
            <NumberInput
              label="Payment terms" suffix=" days" min={0} max={365}
              description="Due this many days after issue."
              value={d.payment_terms_days}
              onChange={(n) => set('payment_terms_days', typeof n === 'number' ? n : 0)}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>Numbering</Title>
          <Text size="sm" c="dimmed">
            {`Statements are numbered prefix, month, then a running number that never resets, such as ${d.statement_prefix || 'ST'}-2026-10-0001. Letters and digits only.`}
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }}>
            <TextInput label={`${v.One('buyer')} statements`} value={d.statement_prefix} onChange={(e) => set('statement_prefix', e.currentTarget.value)} />
            <TextInput label={`${v.One('supplier')} statements`} value={d.supplier_statement_prefix} onChange={(e) => set('supplier_statement_prefix', e.currentTarget.value)} />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack>
          <Title order={4}>How to pay you</Title>
          <Textarea
            label="Bank details"
            description={`Printed on ${v.one('buyer')} statements.`}
            autosize
            minRows={3}
            value={d.bank_details}
            onChange={(e) => set('bank_details', e.currentTarget.value)}
          />
        </Stack>
      </Card>

      <Group justify="flex-end">
        <Button loading={save.isPending} onClick={submit}>Save</Button>
      </Group>
    </Stack>
  );
}
